import { useEffect } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import useLiveResource from "@/hooks/useLiveResource";
import { registerConfigRefresh, registerConfigVersionsGetter, requestConfigRefresh } from "@/lib/configVersion";
import { currentUser } from "@/redux/reducers/AuthReducer";

/**
 * Keeps module entitlements, the unavailable-modules list and the filtered
 * permission set fresh, with no re-login and no token refresh.
 *
 * Four mechanisms, each covering a hole in the previous one:
 *
 * 1. **WebSocket `tenant_config.updated`** — sub-second in the normal case.
 * 2. **`X-Config-Version` on every API response** (wired in `lib/api.js`) —
 *    correctness. Redis down, one of the two FastAPI containers isolated, or a
 *    tab asleep for three hours: the next request the user makes corrects the
 *    UI. This is the mechanism that makes the feature reliable rather than
 *    merely fast.
 * 3. **Refetch on WS reconnect** — covers "it changed while I was
 *    disconnected and I am not currently making requests".
 * 4. **Server-side re-read per request** — authority. Not implemented here;
 *    noted because it is why all of the above is presentation only. There is
 *    no window in which a disabled module is actually usable.
 *
 * Sidebar, routes and `PermissionGuard` all read from the auth slice, so the UI
 * converges with no per-page wiring — including a user sitting on a page they
 * just lost access to, who gets `Unauthorized` on the next render.
 *
 * The store is read through `useStore` rather than `useSelector` so the version
 * getter always returns the *current* values without re-registering (and
 * re-rendering) on every change.
 */
const useConfigVersionSync = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const userId = useSelector((state) => state.auth?.user?.id);

  // Register the getter and the refresh callback with the axios interceptor.
  // Done here rather than inside lib/api.js because that module importing the
  // store would close an import cycle (store → reducers → services → lib/api).
  useEffect(() => {
    const unregisterGetter = registerConfigVersionsGetter(() => {
      const user = store.getState()?.auth?.user;
      return {
        configVersion: user?.configVersion ?? 0,
        platformConfigVersion: user?.platformConfigVersion ?? 0,
      };
    });
    const unregisterRefresh = registerConfigRefresh(() => {
      const auth = store.getState()?.auth;
      // Only refetch for a signed-in user: /auth/current-user needs a token,
      // and a 401 here would clear auth storage and bounce them to login.
      if (!auth?.isAuthenticated) return;
      // One refetch in flight at a time. The 400 ms debounce coalesces a
      // burst, but responses that were already on the wire carry the new
      // header too and arrive after the debounce fired — which produced a
      // second identical /auth/current-user for every single toggle. Nothing
      // is lost by dropping it: if the config moved again meanwhile, the next
      // response's header schedules another one.
      if (auth.isRefreshing) return;
      dispatch(currentUser());
    });
    return () => {
      unregisterGetter();
      unregisterRefresh();
    };
  }, [dispatch, store]);

  // Push, on two keys, through one hook.
  //
  // `tenant_config` — a SuperAdmin changed this tenant's modules or guardian
  // policy, or flipped a module fleet-wide.
  //
  // `user` — an admin edited THIS user's role. The socket used to close on
  // 4002 for that, which is `forceFullLogout()`, so a role edit signed the
  // person out mid-task (I1701 E8). The server now leaves it open and says
  // `user.access_changed`; this is the half that acts on it, through the same
  // `/auth/current-user` refetch mechanism 1 already owns, so the sidebar, the
  // routes and `PermissionGuard` converge with no per-page wiring.
  //
  // Both events are unfiltered and arrive whether or not the key was sent;
  // sending them keeps every call site in the app the same shape. `match` is
  // what narrows the `user` resource to this one event about this one person —
  // a renamed colleague must not refetch anything.
  //
  // `role` — a role's grants were edited. Which role is not narrowed: the
  // event is per-role, the client does not know from the envelope whether it
  // is the role this person holds, and `/auth/current-user` is one request
  // that answers for whatever they hold now. Nothing else covered this —
  // `X-Config-Version` moves on `tenants.config_version`, which only module
  // toggles and the guardian policy bump — so revoking `job.create` from
  // Recruiter left every signed-in recruiter looking at the Add Job button
  // until they signed out. The server was already right: `require_permission`
  // re-resolves from the database on every request, so the button was there
  // and the action refused.
  //
  // One hook rather than two so a reconnect costs one refetch, not one per key:
  // `onChange` also covers the reconnect, since we may have missed the event
  // while disconnected and a replay only helps on reconnect to the same worker.
  useLiveResource({
    keys: ["tenant_config", "user", "role"],
    enabled: Boolean(isAuthenticated),
    match: (envelope) => envelope.resource !== "user" || (envelope.event === "user.access_changed" && String(envelope.id) === String(userId)),
    onChange: requestConfigRefresh,
  });
};

export default useConfigVersionSync;
